/**
 * Application Review Checklist helpers.
 *
 * Kept as pure functions outside AuditProcess/index.jsx so the rules can be unit tested
 * without mounting that component.
 */

/** Checklist item #8, "Reduction of Type (/Integrated)" -- see CHECKLIST_ITEMS in index.jsx. */
export const REDUCTION_OF_TYPE_ITEM_ID = 8;

/**
 * Number of distinct audit standards across a project's scheduling scopes.
 *
 * A project routinely has several scopes for ONE standard (Surveillance 1st/2nd,
 * Recertification are separate scopes of the same ISO/IEC 27001), so the cycles must be
 * collapsed by `standardId` rather than counted as-is. Scopes with no standard id at all
 * (project-level rows, CMF/Fee items) carry no standard and are ignored.
 */
export const countDistinctStandards = (schedulingScopes) => {
  if (!Array.isArray(schedulingScopes)) return 0;
  const ids = schedulingScopes.reduce((acc, scope) => {
    // Grouped Integrated/Combined scopes keep the primary standard in standardId and
    // the remaining standards in includedStandardIds. Count both; otherwise a grouped
    // two-standard project is incorrectly treated as a single-standard project.
    const candidateIds = [
      scope?.standardId,
      ...(Array.isArray(scope?.includedStandardIds) ? scope.includedStandardIds : []),
    ];
    candidateIds.forEach((id) => {
      // String and numeric ids for the same standard reach here from different code paths.
      if (id !== null && id !== undefined && String(id).trim()) acc.add(String(id).trim());
    });
    return acc;
  }, new Set());
  return ids.size;
};

/**
 * "Reduction of Type (/Integrated)" only means anything for a project audited against more
 * than one standard -- there is no second type to reduce against on a single-standard
 * project, so the reviewer has nothing to decide.
 *
 * Returns false while scopes have not loaded yet (empty list, or none carrying a standard):
 * an unknown project must never be locked, only a project we positively know has one
 * standard.
 */
export const isReductionOfTypeNotApplicable = (schedulingScopes) => {
  const standardCount = countDistinctStandards(schedulingScopes);
  return standardCount === 1;
};

/**
 * Fills in checklist item #8 when it does not apply to this project.
 *
 * Returns the same object reference when nothing changes, so it is safe to call from a React
 * setState updater on every render without causing a re-render loop.
 *
 * This has to be re-applied rather than set once: loadChecklistForScope() resets
 * checklistValues to {} twice per scope change (immediately, then again when the response
 * lands), which wiped a one-shot auto-answer and left the item blank with both radios
 * disabled -- blocking Save and Approve with no way for the reviewer to intervene.
 */
export const applyReductionOfTypeAutoAnswer = (checklistValues, isNotApplicable) => {
  const values = checklistValues || {};
  if (!isNotApplicable || values[REDUCTION_OF_TYPE_ITEM_ID]) return checklistValues || {};
  return { ...values, [REDUCTION_OF_TYPE_ITEM_ID]: 'ok' };
};
